import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
  Grow,
  Icon,
  IconButton,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Button
} from '@material-ui/core';
import MUIDataTable from 'mui-datatables';
import logo from 'components/Logo/logo-dermpro.svg';
import { imgBaseUrl } from 'configs';
import useProducts from 'hooks/useProducts';
import { addTasksToQueueApi } from 'api';


const useStyles = makeStyles(() => ({
  root: {
    '& .MuiTableHead-root': {
      position: 'sticky'
    },
    '& > .MuiToolbar-root': {
      borderTop: '1px solid #D0E9FF'
    },
    '& .MuiTableCell-head, & .MuiTableCell-head .MuiButton-root': {
      color: '#4277C6',
      textTransform: 'uppercase',
      fontSize: '12px',
      fontWeight: 600
    },
    '& .MuiTableCell-head': {
      borderColor: '#D0E9FF',
    },
    '& .MuiButton-label *': {
      color: '#4277C6'
    },
    '& .MuiTableCell-body': {
      color: '#777',
      fontWeight: 500
    },
    '& .MuiCheckbox-root': {
      color: '#4B6FC2'
    },
    '& .MuiTableFooter-root': {
      background: '#E7F0FF',
      color: '#4B6FC2',
      '& .MuiSelect-icon, & .MuiTablePagination-root': {
        color: '#4B6FC2'
      },
      '& .MuiIconButton-root.Mui-disabled': {
        color: '#4B6FC2',
        opacity: '0.44'
      }
    }
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    textTransform: 'uppercase',
    fontWeight: 'bold',
    backgroundColor: '#F6F6F9',
  },
  closeButton: {
    background: '#FFFFFF',
    border: '1px solid #DCDCE4',
    borderRadius: '4px',
  },
  closeIcon: {
    color: '#212134',
  },
}));

const CopyRegularListings = ({ open, onClose, allStoreProducts }) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const [catalog, setCatalog] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const { allBrands } = useSelector((state: IReduxState) => state.brands);
  const { profile } = useSelector((state: IReduxState) => state.user);
  const userStore = profile?.stores?.[0];
  const mediaStoreUrl = `${userStore?.apiUrl}/media/catalog/product` || imgBaseUrl;
  const { queueTasksSkus } = useProducts(userStore?.quoteProStoreCode);

  const getNonSelectableReasons = (product) => {
    const reasons = [];

    if (queueTasksSkus.includes(product.sku)) {
      reasons.push('Already in copy queue');
    }

    if (product.sku.includes('GIFT')) {
      reasons.push('Gift card – cannot be copied');
    }

    if (userStore?.quoteProStoreId && product.extension_attributes?.website_ids?.includes(userStore.quoteProStoreId)) {
      reasons.push('Already available in your store');
    }

    if (product.custom_attributes?.find((attr) => attr.attribute_code === 'isoffice')?.value === '1') {
      reasons.push('Internal office item');
    }

    return reasons;
  };

  useEffect(() => {
    if (!allBrands?.length || !allStoreProducts?.length) {
      return;
    }

    const mapped = allStoreProducts.map((product) => {
      const productBrand = product?.custom_attributes?.find((a) => a.attribute_code === 'brand')?.value;
      const productImagePath = product?.custom_attributes?.find((a) => a.attribute_code === 'image')?.value;

      return {
        ...product,
        image: productImagePath ? `${mediaStoreUrl}${productImagePath}` : logo,
        price: Number(parseFloat(product?.price || 0).toFixed(2)),
        brand: allBrands?.find((b) => b.option_id === productBrand)?.value ||
          product?.custom_attributes?.find((attr) => attr.attribute_code === 'catalog_brand_name')?.value,
        status: product?.status === 1 ? 'Enabled' : 'Disabled',
        qty: product?.extension_attributes?.stock_item?.qty || null
      };
    });

    // Sort: selectable products first
    const sorted = mapped.sort((a, b) => {
      const reasonsA = getNonSelectableReasons(a);
      const reasonsB = getNonSelectableReasons(b);
      const selectableA = reasonsA.length === 0;
      const selectableB = reasonsB.length === 0;

      return Number(selectableB) - Number(selectableA); // true = 1 → selectable first
    });

    setCatalog(sorted);
  }, [allStoreProducts, allBrands, mediaStoreUrl, queueTasksSkus, userStore]);

  const convertCopyListingsToTasks = (selectedProducts) => {
    return selectedProducts.flatMap((item) => {
      //all items except gift cards, in office items and already added items, must be available
      if (
        queueTasksSkus.includes(item.sku) ||
        item.sku.includes('GIFT') ||
        (userStore?.quoteProStoreId && item.extension_attributes?.website_ids?.includes(userStore.quoteProStoreId)) ||
        item.custom_attributes?.find((attr) => attr.attribute_code === 'isoffice')?.value === '1'
      ) {
        return [];
      }

      const custom_attributes = item.custom_attributes.filter((attr) => attr.attribute_code === 'image');

      const updatedItem = {
        status: 2, // Magento product status: enabled = 1, disabled = 2
        custom_attributes,
        extension_attributes: {
          website_ids: [...item.extension_attributes.website_ids, userStore.quoteProStoreId],
        }
      };

      return {
        sku: item.sku,
        name: item.name,
        body: updatedItem,
        requestType: 'UPDATE_PRODUCT_TO_ADD_STOREVIEW',
        storeViewCode: userStore.quoteProStoreCode,
      };
    });
  };

  const handleCopyStoreListings = (selectedRows) => {
    const selectedProducts = selectedRows.data.map((row) => catalog[row.dataIndex]);
    const tasks = convertCopyListingsToTasks(selectedProducts);
    addTasksToQueueApi({ dispatch, tasks });
  };

  const columns = [
    {
      name: 'name',
      label: 'Item',
      options: {
        sort: true,
        filter: true,
        customBodyRenderLite: (dataIndex) => {
          const product = catalog[dataIndex];
          return (
            <div className="flex items-center">
              <img
                src={loaded ? product.image : logo}
                alt={product.name}
                style={{maxHeight: '60px', width: '60px', objectFit: 'contain'}}
                className="border-radius-8"
                onLoad={() => setLoaded(true)}
                onError={(e) => {
                  (e.target as HTMLImageElement).onerror = null;
                  (e.target as HTMLImageElement).src = logo;
                }}
              />
              <div className="ml-3">
                <p className="my-0 text-15">{product.name}</p>
              </div>
            </div>
          );
        },
      },
    },
    {
      name: 'qty',
      label: 'Inventory',
      options: {
        filter: true,
        sort: true,
        setCellHeaderProps: () => ({ style: { width: '15%' } }),
        customBodyRenderLite: (dataIndex) => {
          const product = catalog[dataIndex];
          const { qty } = product;
          return (
            <div className="flex items-center">
              <p className="my-0 text-15 capitalize">{qty || ''}</p>
            </div>
          );
        }
      },
    },
    {
      name: 'brand',
      label: 'Brand',
      options: {
        filter: true,
        sort: true,
        setCellHeaderProps: () => ({ style: { width: '15%' } }),
        customBodyRenderLite: (dataIndex) => {
          const product = catalog[dataIndex];
          const { brand } = product;

          return (
            <div className="flex items-center">
              <p className="my-0 text-15 capitalize">{brand}</p>
            </div>
          );
        }
      },
    },
    {
      name: 'price',
      label: 'Price',
      options: {
        filter: true,
        sort: true,
        setCellHeaderProps: () => ({ style: { width: '10%' }, className: 'whitespace-pre'}),
        setCellProps: () => ({className: 'whitespace-pre'}),
        customBodyRenderLite: (dataIndex) => {
          const product = catalog[dataIndex];
          return (
            <div className="flex items-center">
              {`$${Number(product.price) ? product.price.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,') : product.price}`}
            </div>
          );
        },
      },
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        filter: false,
        sort: true,
        filterType: 'dropdown',
        setCellHeaderProps: () => ({ style: { width: '10%'}}),
        customBodyRenderLite: (dataIndex) => {
          const product = catalog[dataIndex];
          return (
            <Chip label={product.status} color={product.status === 'Enabled' ? 'primary' : 'default'} />
          );
        },
      },
    }
  ];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <span>Copy My Listings from My Store</span>
        <IconButton edge="end" size="small" onClick={onClose} className={classes.closeButton}>
          <Icon fontSize="small" className={classes.closeIcon}>close</Icon>
        </IconButton>
      </DialogTitle>

      <DialogContent className="p-0">
        <MUIDataTable
          className={classes.root}
          data={catalog}
          columns={columns}
          options={{
            filterArrayFullMatch: false,
            filterType: 'textField',
            responsive: 'standard',
            download: false,
            search: true,
            filter: true,
            print: false,
            pagination: true,
            viewColumns: false,
            elevation: 0,
            searchPlaceholder: 'Enter product name',
            rowsPerPage: 20,
            rowsPerPageOptions: [20, 40, 80, 100],
            isRowSelectable: (dataIndex) => {
              const reasons = getNonSelectableReasons(catalog[dataIndex]);
              return reasons.length === 0;
            },

            setRowProps: (_row, dataIndex) => {
              const reasons = getNonSelectableReasons(catalog[dataIndex]);

              if (reasons.length > 0) {
                return {
                  style: { opacity: 0.5 },
                  title: `Cannot copy this product:\n${reasons.map((r) => `• ${r}`).join('\n')}`,
                };
              }

              return {};
            },

            customSearchRender: (searchText, handleSearch, hideSearch) => (
              <Grow appear in timeout={300}>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  onChange={({ target: { value } }) =>
                    handleSearch(value)
                  }
                  InputProps={{
                    style: {
                      paddingRight: 0,
                    },
                    startAdornment: (
                      <Icon
                        className="mr-2"
                        fontSize="small"
                      >
                        search
                      </Icon>
                    ),
                    endAdornment: (
                      <IconButton
                        onClick={hideSearch}
                      >
                        <Icon fontSize="small">
                          clear
                        </Icon>
                      </IconButton>
                    ),
                  }}
                />
              </Grow>
            ),
            customToolbarSelect: (selectedRows) => (
              <div>
                <Button
                  className="mr-3"
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    handleCopyStoreListings(selectedRows);
                    onClose();
                  }}
                >
                  Copy Listings
                </Button>
              </div>
            ),
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default CopyRegularListings;
